import { useState, useEffect } from 'react'
import { setCampaignCapping } from '../services/api'

interface CappingCellProps {
  campaignName: string
  value: number | null
  onSaved: (campaignName: string, value: number | null) => void
}

/**
 * H2 del roadmap (Fase A #4): capping manual por campaña, editable en la
 * misma tabla donde ya se ven payout y validation rate. Entrada manual
 * (D4) — campo vacío si la campaña no tiene capping configurado, no un 0.
 *
 * Guarda al perder el foco (blur), no en cada tecla, para no disparar una
 * petición por dígito escrito. Si falla, revierte al último valor guardado.
 */
const CappingCell = ({ campaignName, value, onSaved }: CappingCellProps) => {
  const [draft, setDraft] = useState(value === null ? '' : String(value))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Si el valor llega actualizado desde el padre (ej. otra fila de la misma
  // campaña tras refiltrar), sincronizar el campo.
  useEffect(() => {
    setDraft(value === null ? '' : String(value))
  }, [value])

  const handleBlur = async () => {
    const trimmed = draft.trim()
    const newValue = trimmed === '' ? null : Number(trimmed)

    if (newValue !== null && (!Number.isInteger(newValue) || newValue < 0)) {
      setError('Debe ser un entero ≥ 0')
      return
    }

    setError(null)
    if (newValue === value) {
      return // sin cambios reales, no llamar a la API
    }

    setSaving(true)
    try {
      await setCampaignCapping(campaignName, newValue)
      onSaved(campaignName, newValue)
    } catch (err) {
      console.error('Error guardando capping:', err)
      setError('Error al guardar')
      setDraft(value === null ? '' : String(value)) // revertir al valor previo
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      {/* Input propio, no .filter-select: esa clase trae el fondo con la
          flecha de un <select> y padding pensado para ella — con un
          <input type="number"> se veía como un desplegable falso y
          truncaba el placeholder ("Sin capping" → "Sin ca"). */}
      <input
        type="number"
        min={0}
        step={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        placeholder="Sin capping"
        disabled={saving}
        aria-label={`Capping de ${campaignName}`}
        className="w-32 px-2 py-1.5 text-sm border border-dashboard-border rounded-lg text-dashboard-text focus:ring-2 focus:ring-globo-500 focus:border-globo-500 transition-all duration-200 disabled:opacity-50"
      />
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  )
}

export default CappingCell
